import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../../supabase";
import { useAuth } from "../../contexts/AuthContext";

const BACK_PATH = "/employee/job-applications-tracker";

const parseApplicationRouteId = (raw) => {
    if (!raw || typeof raw !== "string") return null;
    if (raw.startsWith("internal-")) {
        return { type: "internal", applicationRowId: raw.slice("internal-".length) };
    }
    if (raw.startsWith("external-")) {
        return { type: "external", applicationRowId: raw.slice("external-".length) };
    }
    return null;
};

const steps = [
    { key: "application", label: "Application", status: "applied", stage: "Applications" },
    { key: "shortlist", label: "Shortlist", status: "screening", stage: "Shortlist" },
    { key: "interview", label: "Interview", status: "interview", stage: "Interview" },
    { key: "test", label: "Test", status: "assessment", stage: "Test" },
    { key: "hired", label: "Hired", status: "hired", stage: "Hired" },
];

const internalStatusToStepIndex = (status) => {
    const statusToStepMap = {
        applied: 0,
        screening: 1,
        interview: 2,
        assessment: 3,
        offer: 3,
        hired: 4,
        rejected: -1,
        "no responses": 0,
    };
    return statusToStepMap[status] ?? 0;
};

const safeJsonParse = (raw, fallback = []) => {
    if (raw == null || raw === "") return fallback;
    try {
        const v = typeof raw === "string" ? JSON.parse(raw) : raw;
        return Array.isArray(v) ? v : fallback;
    } catch {
        return fallback;
    }
};

const buildCandidateDataFromProfile = (userProfile) => {
    const firstName = userProfile.firstname || "";
    const lastName = userProfile.lastname || "";
    const fullName =
        firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || "Employee User";

    const skills = safeJsonParse(userProfile.skills, []);
    const languages = safeJsonParse(userProfile.languages, []);
    const workExperiences = safeJsonParse(userProfile.work_experiences, []);
    const educations = safeJsonParse(userProfile.educations, []);
    const certifications = safeJsonParse(userProfile.certifications, []);
    const selectedRoles = safeJsonParse(userProfile.selected_roles, []);

    let profileImageUrl = "";
    if (userProfile.profile_image_url) {
        if (userProfile.profile_image_url.startsWith("http")) {
            profileImageUrl = userProfile.profile_image_url;
        } else {
            const { data: imageData } = supabase.storage
                .from("profile-images")
                .getPublicUrl(userProfile.profile_image_url);
            profileImageUrl = imageData.publicUrl;
        }
    }

    return {
        fullName,
        headline: userProfile.headline || "",
        email: userProfile.email || "",
        location: userProfile.location_city_country || "Location not specified",
        linkedinUrl: userProfile.linkedinurl || "",
        profileImageUrl,
        hpcsaRegNo: (userProfile.hpcsa_reg_no || "").trim(),
        hpcsaVerified: (() => {
            const value = userProfile.hpcsa_verified;
            return (
                value === true ||
                value === "true" ||
                value === 1 ||
                value === "1" ||
                value === "t" ||
                value === "T"
            );
        })(),
        aboutText: userProfile.profile_about || "Profile information not available",
        skills,
        workExperiences,
        educations,
        certifications,
        languages,
        rolesOpenTo: selectedRoles,
    };
};

const LinkedInIcon = () => (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
);

const ApplicationProfileView = () => {
    const { id: routeId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [candidateData, setCandidateData] = useState(null);
    const [applicationData, setApplicationData] = useState(null);
    const [jobListingData, setJobListingData] = useState(null);
    const [jobSummary, setJobSummary] = useState(null);
    const [applicationType, setApplicationType] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [isDisqualified, setIsDisqualified] = useState(false);

    const parsed = useMemo(() => parseApplicationRouteId(routeId), [routeId]);

    const loadApplicationContext = useCallback(async () => {
        if (!parsed || !user?.id) return;

        try {
            if (parsed.type === "internal") {
                const { data: application, error: appError } = await supabase
                    .from("applications")
                    .select("*")
                    .eq("id", parsed.applicationRowId)
                    .eq("user_id", user.id)
                    .single();

                if (appError || !application) {
                    throw new Error("Application not found or you do not have access.");
                }

                setApplicationData(application);
                setApplicationType("internal");

                const status = application.status || "applied";
                setIsDisqualified(status === "rejected");
                setCurrentStepIndex(internalStatusToStepIndex(status));

                const { data: jobListing, error: jobError } = await supabase
                    .from("internal_job_listings")
                    .select(
                        "id, title, company_name, screening_questions_list, screening_questions, drivers_license_required, visa_sponsorship_offered"
                    )
                    .eq("id", application.job_listing_id)
                    .single();

                if (!jobError && jobListing) {
                    setJobListingData(jobListing);
                    setJobSummary({
                        title: jobListing.title,
                        company: jobListing.company_name,
                    });
                }
                return;
            }

            const { data: row, error: extError } = await supabase
                .from("my_applications")
                .select(
                    `
                    *,
                    jobs:job_id (
                        id,
                        company,
                        location,
                        title,
                        fulltime_parttime,
                        deadline,
                        application_url
                    )
                `
                )
                .eq("id", parsed.applicationRowId)
                .eq("user_id", user.id)
                .single();

            if (extError || !row) {
                throw new Error("Application not found or you do not have access.");
            }

            setApplicationData(row);
            setApplicationType("external");
            setJobListingData(null);
            const st = row.status || "applied";
            setIsDisqualified(st === "rejected");
            setCurrentStepIndex(0);

            if (row.jobs) {
                setJobSummary({
                    title: row.jobs.title,
                    company: row.jobs.company,
                    location: row.jobs.location,
                });
            }
        } catch (err) {
            setError(err.message || "Something went wrong.");
        }
    }, [parsed, user?.id]);

    const loadOwnProfile = useCallback(async () => {
        if (!user?.id) return;

        const { data: userProfile, error: profileError } = await supabase
            .from("users_profiles")
            .select(
                `
                profileid, role, email, firstname, lastname, headline,
                profession, location_city_country, profile_about,
                profile_image_url, linkedinurl,
                hpcsa_reg_no, hpcsa_verified, skills, languages,
                work_experiences, educations, certifications,
                selected_roles, profile_visibility
            `
            )
            .eq("profileid", user.id)
            .eq("role", "Employee")
            .single();

        if (profileError || !userProfile) {
            throw new Error("Could not load your profile.");
        }

        const visibility = userProfile.profile_visibility;
        if (visibility === "hidden") {
            throw new Error("Your profile is hidden.");
        }

        setCandidateData(buildCandidateDataFromProfile(userProfile));
    }, [user?.id]);

    useEffect(() => {
        if (!user?.id) return;
        if (!parsed) {
            setError("Invalid application link.");
            setLoading(false);
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                setLoading(true);
                setError(null);
                await loadOwnProfile();
                if (!cancelled) await loadApplicationContext();
            } catch (err) {
                if (!cancelled) setError(err.message || "Failed to load.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [user?.id, parsed, loadApplicationContext, loadOwnProfile]);

    const getExternalStatusLabel = (status) => {
        if (!status) return "Applied";
        return status.charAt(0).toUpperCase() + status.slice(1);
    };

    const getInternalStatusLabel = (status) => {
        if (!status) return "Applied";
        if (status === "screening") return "Shortlist";
        return status.charAt(0).toUpperCase() + status.slice(1);
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center employee-application-profile-view-page">
                <p className="text-gray-700">Please log in to view this application.</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center employee-application-profile-view-page">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
            </div>
        );
    }

    if (error || !candidateData) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 employee-application-profile-view-page">
                <div className="text-center max-w-md">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Unable to load application</h2>
                    <p className="text-gray-600 mb-6">{error || "No profile data."}</p>
                    <button
                        type="button"
                        onClick={() => navigate(BACK_PATH)}
                        className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                    >
                        Back to applications
                    </button>
                </div>
            </div>
        );
    }

    const isOpenToWork = true;

    const showInternalApplicationDetails = applicationType === "internal" && applicationData;

    return (
        <>
            <style>{`
            @media (max-width: 1024px) {
                .employee-application-profile-page {
                    padding: 1rem !important;
                }
                .employee-application-profile-container {
                    padding: 0 !important;
                }
                .employee-application-profile-main-grid {
                    grid-template-columns: 1fr !important;
                    gap: 1rem !important;
                }
            }
        `}</style>
            <div className="min-h-screen bg-gray-50 py-8 employee-application-profile-page">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 employee-application-profile-container">
                    <button
                        type="button"
                        onClick={() => navigate(BACK_PATH)}
                        className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
                    >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                        Back to applications
                    </button>

                    <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 bg-gray-200 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0">
                                    {candidateData.profileImageUrl ? (
                                        <img
                                            src={candidateData.profileImageUrl}
                                            alt="Profile"
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <span className="text-lg font-medium text-gray-500">
                                            {candidateData.fullName
                                                ? candidateData.fullName
                                                      .split(" ")
                                                      .map((n) => n[0])
                                                      .join("")
                                                      .toUpperCase()
                                                : "U"}
                                        </span>
                                    )}
                                </div>
                                <div>
                                    {isOpenToWork && <div className="text-xs text-gray-600">Open to work</div>}
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <div className="text-xl font-semibold text-gray-900">{candidateData.fullName}</div>
                                        {candidateData.hpcsaRegNo &&
                                            typeof candidateData.hpcsaRegNo === "string" &&
                                            candidateData.hpcsaRegNo.trim().length > 0 && (
                                                <img
                                                    src="/SealCheck.svg"
                                                    alt={
                                                        candidateData.hpcsaVerified
                                                            ? "HPCSA Verified"
                                                            : "HPCSA Pending Verification"
                                                    }
                                                    className={`w-5 h-5 transition-all ${
                                                        candidateData.hpcsaVerified ? "opacity-100" : "opacity-30 grayscale"
                                                    }`}
                                                    title={
                                                        candidateData.hpcsaVerified
                                                            ? "HPCSA Verified"
                                                            : "HPCSA Verification Pending"
                                                    }
                                                />
                                            )}
                                    </div>
                                    {candidateData.headline && candidateData.headline.trim() && (
                                        <p className="text-sm text-gray-600 font-medium">{candidateData.headline}</p>
                                    )}
                                    {jobSummary?.title && (
                                        <p className="text-sm text-gray-800 mt-1">
                                            <span className="font-medium">Application:</span> {jobSummary.title}
                                            {jobSummary.company ? (
                                                <span className="text-gray-600"> · {jobSummary.company}</span>
                                            ) : null}
                                        </p>
                                    )}
                                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 mt-1">
                                        <span>{candidateData.location}</span>
                                        {candidateData.linkedinUrl && (
                                            <a
                                                href={candidateData.linkedinUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 hover:underline"
                                            >
                                                <LinkedInIcon />
                                                <span>LinkedIn</span>
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col items-end gap-2 text-sm text-gray-700 md:min-w-[140px]">
                                {applicationData && applicationType === "internal" && (
                                    <span className="inline-flex px-3 py-1 rounded-full bg-gray-100 text-gray-900 font-medium">
                                        Status: {getInternalStatusLabel(applicationData.status)}
                                    </span>
                                )}
                                {applicationData && applicationType === "external" && (
                                    <span className="inline-flex px-3 py-1 rounded-full bg-gray-100 text-gray-900 font-medium">
                                        Status: {getExternalStatusLabel(applicationData.status)}
                                    </span>
                                )}
                            </div>
                        </div>

                        {applicationType === "internal" && applicationData && (
                            <div className="mt-6">
                                <div className="text-sm text-gray-600 mb-2">Hiring progress</div>
                                <div className="grid grid-cols-5 gap-6">
                                    {steps.map((s, idx) => {
                                        const isActive = !isDisqualified && idx <= currentStepIndex;
                                        return (
                                            <div key={s.key}>
                                                <div className="text-xs text-gray-600 mb-1">{s.label}</div>
                                                <div
                                                    className={`h-1 rounded-full ${
                                                        isDisqualified ? "bg-red-500" : isActive ? "bg-black" : "bg-gray-200"
                                                    }`}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                                {isDisqualified && (
                                    <div className="mt-2 text-xs text-red-600 font-medium">
                                        This application is no longer active.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 employee-application-profile-main-grid">
                        <div className="lg:col-span-2 space-y-6">
                            {showInternalApplicationDetails && (
                                <div className="bg-white rounded-xl border border-gray-200 p-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                        <div className="border border-gray-200 rounded-lg p-4">
                                            <div className="text-lg font-semibold text-gray-900 mb-3">CV</div>
                                            {applicationData.cv_url ? (
                                                <div className="flex items-center gap-2">
                                                    <a
                                                        href={applicationData.cv_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline truncate flex-1 min-w-0"
                                                    >
                                                        {applicationData.cv_url}
                                                    </a>
                                                    <a
                                                        href="#"
                                                        onClick={async (e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            try {
                                                                const response = await fetch(applicationData.cv_url);
                                                                const blob = await response.blob();
                                                                const url = window.URL.createObjectURL(blob);
                                                                const link = document.createElement("a");
                                                                link.href = url;
                                                                link.download = applicationData.cv_filename || "CV.pdf";
                                                                link.style.display = "none";
                                                                document.body.appendChild(link);
                                                                link.click();
                                                                document.body.removeChild(link);
                                                                window.URL.revokeObjectURL(url);
                                                            } catch {
                                                                const fallbackLink = document.createElement("a");
                                                                fallbackLink.href = applicationData.cv_url;
                                                                fallbackLink.download = applicationData.cv_filename || "CV.pdf";
                                                                fallbackLink.style.display = "none";
                                                                document.body.appendChild(fallbackLink);
                                                                fallbackLink.click();
                                                                document.body.removeChild(fallbackLink);
                                                            }
                                                            return false;
                                                        }}
                                                        className="cursor-pointer hover:text-gray-700 transition-colors"
                                                        title="Download CV"
                                                    >
                                                        <svg
                                                            className="w-4 h-4 text-gray-500 flex-shrink-0"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                                                            />
                                                        </svg>
                                                    </a>
                                                </div>
                                            ) : (
                                                <p className="text-gray-500 italic text-sm">No CV uploaded</p>
                                            )}
                                        </div>

                                        <div className="border border-gray-200 rounded-lg p-4">
                                            <div className="text-lg font-semibold text-gray-900 mb-3">Cover Letter</div>
                                            {applicationData.cover_letter_url ? (
                                                <div className="flex items-center gap-2">
                                                    <a
                                                        href={applicationData.cover_letter_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline truncate flex-1 min-w-0"
                                                    >
                                                        {applicationData.cover_letter_url}
                                                    </a>
                                                    <a
                                                        href="#"
                                                        onClick={async (e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            try {
                                                                const response = await fetch(applicationData.cover_letter_url);
                                                                const blob = await response.blob();
                                                                const url = window.URL.createObjectURL(blob);
                                                                const link = document.createElement("a");
                                                                link.href = url;
                                                                link.download =
                                                                    applicationData.cover_letter_filename || "Cover Letter.pdf";
                                                                link.style.display = "none";
                                                                document.body.appendChild(link);
                                                                link.click();
                                                                document.body.removeChild(link);
                                                                window.URL.revokeObjectURL(url);
                                                            } catch {
                                                                const fallbackLink = document.createElement("a");
                                                                fallbackLink.href = applicationData.cover_letter_url;
                                                                fallbackLink.download =
                                                                    applicationData.cover_letter_filename || "Cover Letter.pdf";
                                                                fallbackLink.style.display = "none";
                                                                document.body.appendChild(fallbackLink);
                                                                fallbackLink.click();
                                                                document.body.removeChild(fallbackLink);
                                                            }
                                                            return false;
                                                        }}
                                                        className="cursor-pointer hover:text-gray-700 transition-colors"
                                                        title="Download Cover Letter"
                                                    >
                                                        <svg
                                                            className="w-4 h-4 text-gray-500 flex-shrink-0"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                                                            />
                                                        </svg>
                                                    </a>
                                                </div>
                                            ) : (
                                                <p className="text-gray-500 italic text-sm">No cover letter uploaded</p>
                                            )}
                                        </div>
                                    </div>

                                    {jobListingData ? (
                                        <div className="border-t border-gray-200 pt-6">
                                            <div className="text-lg font-semibold text-gray-900 mb-3">Screening questions</div>
                                            <div className="space-y-4">
                                                {jobListingData.screening_questions_list &&
                                                    Array.isArray(jobListingData.screening_questions_list) &&
                                                    jobListingData.screening_questions_list.length > 0 && (
                                                        <>
                                                            {jobListingData.screening_questions_list.map((questionObj, qIndex) => {
                                                                const answer =
                                                                    applicationData.screening_answers?.[qIndex] ||
                                                                    applicationData.screening_answers?.[`${qIndex}`];
                                                                const questionType =
                                                                    questionObj.type ||
                                                                    (questionObj.options ? "multiple-choice" : "text");
                                                                return (
                                                                    <div
                                                                        key={qIndex}
                                                                        className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0"
                                                                    >
                                                                        <div className="text-sm font-medium text-gray-900 mb-2">
                                                                            {questionObj.question}
                                                                        </div>
                                                                        <div className="text-sm text-gray-700">
                                                                            {questionType === "text" ? (
                                                                                <div>
                                                                                    <span className="font-medium block mb-1">
                                                                                        Answer:
                                                                                    </span>
                                                                                    <div className="bg-gray-50 rounded-lg p-3 whitespace-pre-wrap">
                                                                                        {answer || "Not answered"}
                                                                                    </div>
                                                                                </div>
                                                                            ) : (
                                                                                <span>
                                                                                    Answer:{" "}
                                                                                    <span className="font-medium">
                                                                                        {answer || "Not answered"}
                                                                                    </span>
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </>
                                                    )}

                                                {jobListingData.drivers_license_required && (
                                                    <div className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                                                        <div className="text-sm font-medium text-gray-900 mb-2">
                                                            Do you have a valid driver&apos;s license?
                                                        </div>
                                                        <div className="text-sm text-gray-700">
                                                            Answer:{" "}
                                                            <span className="font-medium">
                                                                {applicationData.drivers_license_answer || "Not answered"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}

                                                {jobListingData.visa_sponsorship_offered && (
                                                    <div className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                                                        <div className="text-sm font-medium text-gray-900 mb-2">
                                                            Do you require visa sponsorship?
                                                        </div>
                                                        <div className="text-sm text-gray-700">
                                                            Answer:{" "}
                                                            <span className="font-medium">
                                                                {applicationData.visa_sponsorship_answer || "Not answered"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}

                                                {applicationData.eligible_to_work !== null && (
                                                    <div className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                                                        <div className="text-sm font-medium text-gray-900 mb-2">
                                                            Are you legally eligible to work in South Africa?
                                                        </div>
                                                        <div className="text-sm text-gray-700">
                                                            Answer:{" "}
                                                            <span className="font-medium">
                                                                {applicationData.eligible_to_work || "Not answered"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                                {applicationData.clinical_experience !== null && (
                                                    <div className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                                                        <div className="text-sm font-medium text-gray-900 mb-2">
                                                            Do you have clinical experience?
                                                        </div>
                                                        <div className="text-sm text-gray-700">
                                                            Answer:{" "}
                                                            <span className="font-medium">
                                                                {applicationData.clinical_experience || "Not answered"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                                {applicationData.available_on_site !== null && (
                                                    <div className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                                                        <div className="text-sm font-medium text-gray-900 mb-2">
                                                            Are you available to work on-site?
                                                        </div>
                                                        <div className="text-sm text-gray-700">
                                                            Answer:{" "}
                                                            <span className="font-medium">
                                                                {applicationData.available_on_site || "Not answered"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}

                                                {(!jobListingData.screening_questions_list ||
                                                    !Array.isArray(jobListingData.screening_questions_list) ||
                                                    jobListingData.screening_questions_list.length === 0) &&
                                                    !jobListingData.drivers_license_required &&
                                                    !jobListingData.visa_sponsorship_offered &&
                                                    applicationData.eligible_to_work === null &&
                                                    applicationData.clinical_experience === null &&
                                                    applicationData.available_on_site === null && (
                                                        <p className="text-gray-500 italic text-sm">
                                                            No screening questions for this job listing
                                                        </p>
                                                    )}
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            )}

                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                <div className="text-lg font-semibold text-gray-900 mb-3">About</div>
                                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                                    {candidateData.aboutText || "No about information available."}
                                </p>
                            </div>

                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                <div className="text-lg font-semibold text-gray-900 mb-3">Skills</div>
                                {candidateData.skills?.length ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {[0, 1].map((col) => (
                                            <ul key={col} className="space-y-2">
                                                {candidateData.skills
                                                    .slice(
                                                        col === 0 ? 0 : Math.ceil(candidateData.skills.length / 2),
                                                        col === 0
                                                            ? Math.ceil(candidateData.skills.length / 2)
                                                            : candidateData.skills.length
                                                    )
                                                    .map((skill, index) => (
                                                        <li key={`${col}-${index}`} className="flex items-center text-gray-700">
                                                            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3" />
                                                            <span>
                                                                {typeof skill === "string" ? skill : skill.name}
                                                                {typeof skill === "object" && skill.level && (
                                                                    <span className="text-sm text-gray-500 ml-2">
                                                                        ({skill.level})
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </li>
                                                    ))}
                                            </ul>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 italic">No skills added yet.</p>
                                )}
                            </div>

                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                <div className="text-lg font-semibold text-gray-900 mb-3">Experience</div>
                                {candidateData.workExperiences?.length ? (
                                    <div className="space-y-6">
                                        {candidateData.workExperiences.map((experience, index) => (
                                            <div key={index} className="border-l-4 border-gray-900 pl-4">
                                                <div className="font-semibold text-gray-900">{experience.title || "[Role]"}</div>
                                                <div className="text-gray-600">{experience.company || "Company Name"}</div>
                                                <div className="text-gray-500 text-sm">
                                                    {experience.startDate && (experience.endDate || experience.isCurrent)
                                                        ? `${experience.startDate} to ${
                                                              experience.isCurrent ? "Present" : experience.endDate
                                                          }`
                                                        : "Date not specified"}
                                                </div>
                                                {experience.description && (
                                                    <p className="text-gray-700 mt-2 whitespace-pre-wrap">{experience.description}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 italic">No work experience added yet.</p>
                                )}
                            </div>

                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                <div className="text-lg font-semibold text-gray-900 mb-3">Education</div>
                                {candidateData.educations?.length ? (
                                    <div className="space-y-6">
                                        {candidateData.educations.map((education, index) => (
                                            <div key={index} className="border-l-4 border-gray-900 pl-4">
                                                <div className="font-semibold text-gray-900">
                                                    {education.fieldOfStudy || education.degree || "[Course name]"}
                                                </div>
                                                <div className="text-gray-600">{education.school || "Institution Name"}</div>
                                                <div className="text-gray-500 text-sm">
                                                    {education.startDate && (education.endDate || education.isCurrent)
                                                        ? `${education.startDate} - ${
                                                              education.isCurrent ? "Present" : education.endDate
                                                          }`
                                                        : "Date not specified"}
                                                </div>
                                                {education.description && (
                                                    <p className="text-gray-700 mt-2 whitespace-pre-wrap">{education.description}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 italic">No education information added yet.</p>
                                )}
                            </div>

                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                <div className="text-lg font-semibold text-gray-900 mb-3">Certifications</div>
                                {candidateData.certifications?.length ? (
                                    <div className="space-y-4">
                                        {candidateData.certifications.map((certification, index) => (
                                            <div key={index} className="border-l-4 border-gray-900 pl-4">
                                                <div className="font-semibold text-gray-900">
                                                    {certification.courseName || "[Course name]"}
                                                </div>
                                                <div className="text-gray-600">{certification.issuingOrg || "Institution Name"}</div>
                                                <div className="text-gray-500 text-sm">
                                                    {certification.issueDate ? `Issued: ${certification.issueDate}` : "Issued: date"}
                                                </div>
                                                {certification.credentialId && (
                                                    <div className="text-gray-500 text-sm">
                                                        Credential ID: {certification.credentialId}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 italic">No certifications added yet.</p>
                                )}
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                <div className="text-lg font-semibold text-gray-900 mb-3">Roles open to:</div>
                                {candidateData.rolesOpenTo?.length ? (
                                    <div className="flex flex-wrap gap-2">
                                        {candidateData.rolesOpenTo.map((role, index) => (
                                            <span
                                                key={index}
                                                className="px-3 py-1 rounded-full bg-gray-100 text-gray-800 text-sm"
                                            >
                                                {role}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 italic">No roles specified yet.</p>
                                )}
                            </div>

                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                <div className="text-lg font-semibold text-gray-900 mb-3">Languages spoken</div>
                                {candidateData.languages?.length ? (
                                    <ul className="space-y-2">
                                        {candidateData.languages.map((language, index) => (
                                            <li key={index} className="flex items-center text-gray-700">
                                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3" />
                                                {language}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-gray-500 italic">No languages added yet.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ApplicationProfileView;
